import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import grades from '../data/grades';
import { latestIssueByGrade } from '../data/newsletters';

const ArrowRightIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
  </svg>
);

// Alt text describes what is in each photo. The teachers aren't named because
// no names were provided with the photos — add them here when they are.
const PHOTO_ALT = {
  'pre-k': 'The Pre-Kindergarten teaching team standing together in a colorful PS 85 hallway.',
  'kindergarten': 'The Kindergarten teaching team posing together in their classroom.',
  'grade-1': 'The 1st Grade teaching team standing together in front of a classroom library.',
  'grade-2': 'The 2nd Grade teaching team gathered in front of a classroom alphabet display.',
  'grade-3': 'The 3rd Grade teaching team posing together in a classroom, some seated at the front.',
  'grade-4': 'The 4th Grade teaching team standing together in front of a classroom whiteboard.',
  'grade-5': 'The 5th Grade teaching team standing together in a brightly decorated classroom.',
};

const OurClasses = () => {
  // Deep links like /our-classes#grade-2 (from the navbar). The prerendered
  // HTML handles this on its own; this covers the client-rendered dev server.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    // 'instant': the site sets scroll-behavior: smooth, which would otherwise
    // animate the whole page from the top on load.
    if (id) document.getElementById(id)?.scrollIntoView({ behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen font-sans text-gray-800 selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* Hero */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden" style={{ backgroundColor: '#0F2240' }}>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -right-20 -top-20 w-80 h-80 bg-school-gold/12 rounded-full blur-3xl" />
            <div className="absolute -left-10 bottom-0 w-60 h-60 bg-school-gold/6 rounded-full blur-2xl" />
          </div>
          <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
            <span className="text-school-gold font-bold tracking-[0.2em] uppercase text-xs mb-4 block">
              Our Classes
            </span>
            <h1
              className="font-heading font-bold text-white tracking-tight leading-[1.05] mb-6"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
            >
              Meet Our{' '}
              <em className="text-school-gold font-light not-italic">Grade Teams</em>
            </h1>
            <p
              className="text-white/55 font-light leading-relaxed max-w-xl mx-auto"
              style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)' }}
            >
              The teachers guiding our students from Pre-K through 5th grade this year.
            </p>

            {/* Jump to a grade */}
            <nav aria-label="Grade teams" className="mt-10">
              <ul className="flex flex-wrap justify-center gap-2">
                {grades.map((g) => (
                  <li key={g.id}>
                    <a
                      href={`#${g.id}`}
                      className="inline-block px-4 py-2 rounded-full bg-white/8 border border-white/15 text-white text-sm font-semibold hover:bg-white/15 transition-colors focus:outline-none focus:ring-2 focus:ring-school-gold"
                    >
                      {g.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </section>

        {/* One section per grade team, photo side alternating on desktop */}
        <div className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-20 md:space-y-28">
            {grades.map((g, i) => (
              <section
                key={g.id}
                id={g.id}
                aria-labelledby={`${g.id}-heading`}
                className="scroll-mt-28 grid lg:grid-cols-12 gap-8 lg:gap-14 items-center"
              >
                <figure className={`lg:col-span-7 ${i % 2 ? 'lg:order-2' : ''}`}>
                  <img
                    src={g.photo.src}
                    width={g.photo.width}
                    height={g.photo.height}
                    alt={PHOTO_ALT[g.id]}
                    loading={i < 2 ? 'eager' : 'lazy'}
                    className="w-full h-auto rounded-3xl shadow-xl shadow-school-blue/10 border-4 border-white"
                  />
                </figure>

                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3 mb-5">
                    <span
                      className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 text-white font-bold text-sm"
                      style={{ backgroundColor: g.color }}
                      aria-hidden="true"
                    >
                      {g.short}
                    </span>
                    <span className="text-school-goldDark font-bold tracking-[0.18em] uppercase text-xs">
                      Grade Team
                    </span>
                  </div>
                  <h2
                    id={`${g.id}-heading`}
                    className="font-heading font-bold text-school-blue tracking-tight leading-[1.05] mb-4"
                    style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
                  >
                    {g.fullLabel}
                  </h2>
                  <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8">
                    Meet the teachers guiding our {g.label} learners this school year.
                  </p>
                  {latestIssueByGrade[g.id] && (
                    <a
                      href={`/newsletters#${g.id}`}
                      className="group inline-flex items-center gap-2 bg-school-blue text-white px-6 py-3 rounded-full font-bold text-sm hover:bg-[#0F2240] transition-colors focus:outline-none focus:ring-2 focus:ring-school-blue/40 focus:ring-offset-2"
                    >
                      Read the {latestIssueByGrade[g.id].month.split(' ')[0]} newsletter
                      <span className="sr-only"> from the {g.fullLabel} team</span>
                      <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </a>
                  )}
                </div>
              </section>
            ))}
          </div>
        </div>

      </main>

      <Footer />
    </div>
  );
};

export default OurClasses;
